import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import { PlusIcon, EditIcon, TrashIcon, CodeIcon, UserIcon } from 'lucide-react'
import { AppHeader } from '@/components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface SmartPhrase {
  id: number
  code: string
  name: string
  content: string
  is_system: boolean
  creator_name: string
  created_at: string
}

interface IndexProps {
  smart_phrases: SmartPhrase[]
}

export default function Index({ smart_phrases }: IndexProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [phraseToDelete, setPhraseToDelete] = useState<SmartPhrase | null>(null)

  const filteredPhrases = smart_phrases.filter(phrase =>
    phrase.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    phrase.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleDelete = (phrase: SmartPhrase) => {
    setPhraseToDelete(phrase)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (phraseToDelete) {
      router.delete(`/admin/smart_phrases/${phraseToDelete.id}`, {
        onSuccess: () => {
          setDeleteDialogOpen(false)
          setPhraseToDelete(null)
        }
      })
    }
  }

  return (
    <>
      <AppHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin/console' },
          { label: 'SmartPhrases' },
        ]}
      />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
              <Card className="bg-gradient-to-t from-primary/5 to-card shadow-xs">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>SmartPhrase Templates</CardTitle>
                      <CardDescription>
                        Reusable note templates for faster clinical documentation
                      </CardDescription>
                    </div>
                    <Button onClick={() => router.visit('/admin/smart_phrases/new')}>
                      <PlusIcon className="mr-2 h-4 w-4" />
                      New Template
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <Input
                      placeholder="Search by code or name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="max-w-sm"
                    />
                  </div>

                  <div className="space-y-4">
                    {filteredPhrases.length === 0 ? (
                      <div className="py-8 text-center text-muted-foreground">
                        {searchQuery ? 'No templates found matching your search.' : 'No templates created yet.'}
                      </div>
                    ) : (
                      filteredPhrases.map((phrase) => (
                        <Card key={phrase.id} className="border-l-4 border-l-primary/20">
                          <CardContent className="pt-6">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 space-y-2">
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary" className="font-mono">
                                    <CodeIcon className="mr-1 h-3 w-3" />
                                    {phrase.code}
                                  </Badge>
                                  {phrase.is_system && (
                                    <Badge variant="default">System Template</Badge>
                                  )}
                                </div>
                                <h3 className="text-lg font-semibold">{phrase.name}</h3>
                                <p className="text-sm text-muted-foreground line-clamp-2">
                                  {phrase.content}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <UserIcon className="h-3 w-3" />
                                  <span>Created by {phrase.creator_name}</span>
                                  <span>•</span>
                                  <span>{new Date(phrase.created_at).toLocaleDateString()}</span>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => router.visit(`/admin/smart_phrases/${phrase.id}/edit`)}
                                >
                                  <EditIcon className="h-4 w-4" />
                                </Button>
                                {!phrase.is_system && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDelete(phrase)}
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete SmartPhrase Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{phraseToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
